import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faChevronRight,
  faKey,
  faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import { ChangePasswordSheet } from '../auth/ChangePasswordSheet';
import { groupFor, systemFor, titleFor } from './navigation';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsMenu } from './NotificationsMenu';
import { homeRouteFor } from '../../routes/homeRoute';
import type { User } from '../../types/api';

interface NavbarProps {
  user: User;
  onToggleMenu: () => void;
  onLogout: () => void;
}

function initials(fullName: string): string {
  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * Navbar superior del shell.
 *
 * La busqueda global esta operativa desde la fase 8 sobre productos, ordenes y
 * obras. Su hueco existia desde la fase 2 para que el shell no cambiara de
 * forma al llegar, y hasta entonces se entrego deshabilitada: un buscador que
 * no encuentra nada solo ensenia a la gente a no usarlo.
 *
 * El contador de notificaciones esta operativo desde la fase 7 y muestra datos
 * reales: incidentes reportados, ordenes cerradas con faltantes y comprobantes
 * que no se pudieron enviar.
 */
export function Navbar({ user, onToggleMenu, onLogout }: NavbarProps): ReactNode {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Las migas nombran el camino completo: sistema, grupo y pantalla. Con
  // varias aplicaciones en el mismo panel, el nombre del sistema es lo que
  // situa al usuario cuando dos pantallas se llaman parecido.
  const ancestors = [
    systemFor(location.pathname)?.label,
    groupFor(location.pathname)?.label,
  ].filter((label): label is string => Boolean(label));
  const title = titleFor(location.pathname);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-surface shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        <button
          type="button"
          onClick={onToggleMenu}
          aria-label="Abrir o contraer la navegacion"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-card text-content hover:bg-primary-soft"
        >
          <FontAwesomeIcon icon={faBars} aria-hidden="true" />
        </button>

        {/*
          Las migas se recortan; nunca ensanchan la página.

          El `min-w-0` de la lista y del último elemento no es decoracion: un
          elemento flexible arranca con `min-width: auto`, así que sin ellos el
          `truncate` no puede encoger y la miga empuja la barra por fuera del
          ancho de la ventana. Aparecio en la revisión de la fase 9, en la
          pantalla de usuarios a 768 px, y afectaba al armazon entero.
        */}
        <nav aria-label="Migas de pan" className="min-w-0 flex-1 overflow-hidden">
          <ol className="flex min-w-0 items-center gap-2 text-sm text-muted">
            <li className="hidden shrink-0 sm:block">
              <Link to={homeRouteFor(user.role)} className="hover:text-content">
                Inicio
              </Link>
            </li>
            {ancestors.map((label) => (
              <Fragment key={label}>
                <li aria-hidden="true" className="hidden shrink-0 sm:block">
                  <FontAwesomeIcon icon={faChevronRight} className="w-2.5" />
                </li>
                <li className="hidden shrink-0 truncate sm:block">{label}</li>
              </Fragment>
            ))}
            <li aria-hidden="true" className="hidden shrink-0 sm:block">
              <FontAwesomeIcon icon={faChevronRight} className="w-2.5" />
            </li>
            <li aria-current="page" className="min-w-0 truncate font-medium text-content">
              {title}
            </li>
          </ol>
        </nav>

        <GlobalSearch />

        <NotificationsMenu />

        <div ref={menuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => {
              setIsMenuOpen((open) => !open);
            }}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            className="flex h-11 items-center gap-2 rounded-card px-2 hover:bg-primary-soft"
          >
            <span
              aria-hidden="true"
              className="grid h-9 w-9 place-items-center rounded-full bg-primary text-xs font-semibold text-white"
            >
              {initials(user.full_name)}
            </span>
            <span className="hidden text-left sm:block">
              <span className="block text-sm font-medium leading-tight text-content">
                {user.full_name}
              </span>
              <span className="block text-xs text-muted">
                {user.role === 'ADMIN' ? 'Administrador' : 'Chofer'}
              </span>
            </span>
          </button>

          {isMenuOpen ? (
            <div
              role="menu"
              className="absolute right-0 z-30 mt-2 w-56 rounded-card border border-line bg-surface py-1 shadow-lg"
            >
              <p className="truncate px-3 py-2 text-xs text-muted">{user.email}</p>
              {/*
                «Mi cuenta» no está: los datos del usuario los administra la
                oficina desde la pantalla de usuarios, así que sería una ficha
                de solo lectura. Lo único que uno hace sobre su propia cuenta
                es cambiar la contraseña.
              */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setIsMenuOpen(false);
                  setIsChangingPassword(true);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-content hover:bg-primary-soft"
              >
                <FontAwesomeIcon icon={faKey} aria-hidden="true" className="w-3.5" />
                Cambiar contraseña
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={onLogout}
                className="flex w-full items-center gap-2 border-t border-line px-3 py-2 text-left text-sm text-content hover:bg-primary-soft"
              >
                <FontAwesomeIcon icon={faRightFromBracket} aria-hidden="true" className="w-3.5" />
                Cerrar sesión
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <ChangePasswordSheet
        isOpen={isChangingPassword}
        onClose={() => {
          setIsChangingPassword(false);
        }}
      />
    </header>
  );
}
